/**
 * Byte → text decoding with BOM and encoding detection.
 *
 * `auto` (the default): UTF-8 BOM / UTF-16 BOM are honoured; otherwise the first ~4 KB is test-decoded as
 * strict UTF-8 and, if that fails, the file is read as windows-1252 (what Excel's "CSV" save produces on
 * Western systems). The decision is made once, from the head of the stream, then applied to every chunk.
 * Bytes that are still invalid later on become U+FFFD and are counted in `CsvMeta.replacementChars`, so a
 * bad file is reported rather than silently mangled.
 */

import type { CsvEncoding, CsvMeta } from "./types";

/** How many bytes to buffer before sniffing the encoding. */
export const SNIFF_BYTES = 4096;

type ResolvedEncoding = Exclude<CsvEncoding, "auto">;

function textDecoderLabel(encoding: ResolvedEncoding): string {
  return encoding === "iso-8859-1" ? "windows-1252" : encoding;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export class ChunkDecoder {
  private head: Uint8Array = new Uint8Array(0);
  private decoder: TextDecoder | null = null;
  private stringBomChecked = false;

  constructor(
    private readonly requested: CsvEncoding,
    private readonly meta: CsvMeta
  ) {}

  /** Pass an already-decoded string chunk through (strips a leading BOM once). */
  passthrough(text: string): string {
    this.meta.encoding ??= "utf-8";
    if (!this.stringBomChecked) {
      this.stringBomChecked = true;
      if (text.charCodeAt(0) === 0xfeff) {
        this.meta.hadBom = true;
        return text.slice(1);
      }
    }
    return text;
  }

  /**
   * Decode a byte chunk. Returns "" while the head is still being buffered for sniffing. Call with
   * `last = true` and an empty array at end of input to flush.
   */
  decode(bytes: Uint8Array, last: boolean): string {
    if (this.decoder === null) {
      this.head = this.head.length === 0 ? bytes : concat(this.head, bytes);
      if (this.head.length < SNIFF_BYTES && !last) return "";
      const head = this.head;
      this.head = new Uint8Array(0);
      return this.start(head, last);
    }
    return this.run(bytes, last);
  }

  private start(head: Uint8Array, last: boolean): string {
    let encoding: ResolvedEncoding | null = this.requested === "auto" ? null : this.requested;
    let offset = 0;

    if (head.length >= 3 && head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf) {
      offset = 3;
      this.meta.hadBom = true;
      if (encoding === null || encoding === "utf-8") encoding = "utf-8";
    } else if (head.length >= 2 && head[0] === 0xff && head[1] === 0xfe) {
      offset = 2;
      this.meta.hadBom = true;
      if (encoding === null || encoding === "utf-16le") encoding = "utf-16le";
    } else if (head.length >= 2 && head[0] === 0xfe && head[1] === 0xff) {
      offset = 2;
      this.meta.hadBom = true;
      if (encoding === null || encoding === "utf-16be") encoding = "utf-16be";
    }

    const body = offset === 0 ? head : head.subarray(offset);
    if (encoding === null) encoding = looksLikeUtf8(body, last) ? "utf-8" : "windows-1252";

    this.meta.encoding = encoding;
    this.decoder = new TextDecoder(textDecoderLabel(encoding), { fatal: false, ignoreBOM: true });
    return this.run(body, last);
  }

  private run(bytes: Uint8Array, last: boolean): string {
    const decoder = this.decoder as TextDecoder;
    const text = decoder.decode(bytes, { stream: !last });
    if (text.indexOf("�") !== -1) {
      let count = 0;
      for (let i = text.indexOf("�"); i !== -1; i = text.indexOf("�", i + 1)) count += 1;
      this.meta.replacementChars += count;
    }
    return text;
  }
}

function looksLikeUtf8(bytes: Uint8Array, last: boolean): boolean {
  try {
    // `stream: !last` lets a multi-byte character cut by the sniff window pass; a truly invalid byte throws.
    new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes, { stream: !last });
    return true;
  } catch {
    return false;
  }
}
